import BodyText from 'LuxKit/Typography/BodyText'
// Subtitle replaced by BodyText
import Image from 'components/Common/Image'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { getStepsHowItWorks, getStepsBuyNow } from './AfterPayLandingPageData'
import { connect } from 'react-redux'
import Error404Page from 'components/Pages/Error404Page'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import { isAfterPayBpEnabled } from 'lib/payment/afterPayBpUtils'
import Heading from 'LuxKit/Typography/Heading'
import OfferList from 'components/OfferList/OfferList'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'

const HowItWorksContent = styled.div`
  margin-top: ${rem(40)};
  width: 100%;
`

const BuyNowContent = styled.div`
  margin-top: ${rem(48)};
  width: 100%;
`

const AppStoreContent = styled.div`
  margin-top: ${rem(20)};
  width: 100%;
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(32)} ${rem(40)};
  max-width: ${rem(1160)};
  width: 100%;
  height: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const HeroImage = styled(ImageHero)`
  max-height: ${rem(480)};
`

const LogoImage = styled(Image)`
  margin-bottom: ${rem(16)};
  image-rendering: -webkit-optimize-contrast;
  transform: translateZ(0);
`

const StoreImage = styled(Image)`
  margin-top: ${rem(20)};
  margin-right: ${rem(6)};
  margin-left: ${rem(6)};
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
`

const Section = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  max-width: 100%;

  &:nth-child(even) {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const SectionCTA = styled(TextButton)`
  margin-top: ${rem(32)};
`

const SectionBlurb = styled(BodyText)`
  margin-top: ${rem(16)};
  max-width: ${rem(750)};
  width: 100%;
`

const ProductDescriptors = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    margin-left: -${rem(30)};
  }
`

const ProductDescriptor = styled.div`
  display: flex;
  flex-direction: column;
  /* Fixes IE overflow issue */
  align-items: stretch;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(24)};
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(30)};
    margin-top: 0;
    height: ${rem(180)};
    flex: 1 1 33%;
  }
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > * + * {
    margin-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      margin-top: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    margin-left: -${rem(32)};
  }
`

const HowItWorksCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  padding: ${rem(110)} ${rem(32)} ${rem(32)};
  max-width: ${rem(415)};
  flex-grow: 1;
  margin-left: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-basis: 100%;
  }
`

const AppLinks = styled(TextLink)`
  display: block;
  padding: 0;
`

const HowItWorksCardImage = styled.div`
  position: absolute;
  top: ${rem(40)};
  left: 0;
  right: 0;
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
`
interface Props {
  currency: string
  theme: any
  isAfterPayBpEnabled: boolean
}

const listFilters: App.OfferListFilters = {
  offerTypes: FLASH_OFFERS_TYPES,
  frontPageVisibleOnly: true,
}

function AfterPayLandingPage(props: Props) {
  const intl = useIntl()

  useEffect(() => {
    window.scroll(0, 0)
  }, [])

  const stepsHowItWorks = getStepsHowItWorks(intl)
  const stepsBuyNow = getStepsBuyNow(intl)
  const afterPayLogoAlt = intl.formatMessage({
    id: 'IbCVo-Q6tZe0kYmevmiR',
    defaultMessage: 'After Pay',
    description: 'Alt text for the Afterpay logo shown on the Afterpay promotional landing page',
  })

  const { isAfterPayBpEnabled } = props

  if (!isAfterPayBpEnabled) {
    return <Error404Page invalidRegion />
  }

  return (
    <div>
      <HeadData title={intl.formatMessage({
        id: 'DoMbiHUYK_Q3qrO0-ZvP',
        defaultMessage: 'Afterpay',
        description: 'Page title for the Afterpay promotional landing page',
      })}
      />
      <HeroImage
        alt=""
        aria-hidden="true"
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="16:5"
        id="7xialy04bpqlb12yu6wm"
        brightness={-50}
      >
        <HeroSectionContent>
          <LogoImage
            height="60"
            id="zc2pf8m6pryfev46h4o"
            alt={afterPayLogoAlt}
            highRes
            quality="good"
          />
          <Heading variant="heading2" align="center" colour="neutral-eight" as="h1">
            {intl.formatMessage({
              id: 'INMqR4g9hewlWeKOZYhb',
              defaultMessage: 'Afterpay, a better way to pay.',
              description: 'Main heading on Afterpay landing page hero section',
            })}
          </Heading>
          <SectionBlurb variant="large" align="center" colour="neutral-eight">
            {intl.formatMessage({
              id: 'niYAw_CfUT6V6KVJukEI',
              defaultMessage: 'Book now. Pay over six weeks. Never pay interest.',
              description: 'Hero blurb on Afterpay landing page on the promotional landing page',
            })}
          </SectionBlurb>
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <BodyText variant="large" align="center" as="h2">
            {intl.formatMessage({
              id: 'UVeuPs4pKxqCOg3JqP-Y',
              defaultMessage: 'How it works',
              description: 'Section heading for How it works on Afterpay landing page',
            })}
          </BodyText>
          <HowItWorksContent>
            <ColumnContainer>
              {stepsHowItWorks.map((step, index) => (
                <HowItWorksCard key={index}>
                  <HowItWorksCardImage>{step.image}</HowItWorksCardImage>
                  <BodyText variant="large">{step.title}</BodyText>
                  <BodyText variant="large">
                    {step.description}
                  </BodyText>
                </HowItWorksCard>
              ))}
            </ColumnContainer>
            <SubSection>
              <TextButton kind="primary" target="_blank" fit="mobile-full-width" href="https://www.afterpay.com/en-AU/how-it-works">
                {intl.formatMessage({
                  id: 'G5QM5UuiZVoE7_GTu3wA',
                  defaultMessage: 'Get started with Afterpay',
                  description: 'CTA button to learn more about Afterpay on the promotional landing page',
                })}
              </TextButton>
            </SubSection>
          </HowItWorksContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <BodyText variant="large" align="center" as="h2">
            {intl.formatMessage({
              id: 'Cqp2avzz9sWnCFcetopu',
              defaultMessage: 'Buy now, stay in luxury',
              description: 'Section heading for Buy now section on Afterpay landing page',
            })}
          </BodyText>
          <BuyNowContent>
            <ProductDescriptors>
              {stepsBuyNow.map((step, index) => (
                <ProductDescriptor key={index}>
                  <BodyText variant="large">{step.title}</BodyText>
                  <BodyText variant="large">{step.description}</BodyText>
                </ProductDescriptor>
              ))}
            </ProductDescriptors>
          </BuyNowContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">
            {intl.formatMessage({
              id: 'kfTChAsZiuU0czWDeL-b',
              defaultMessage: 'Afterpay it with the app!',
              description: 'Section heading for Afterpay app section on the landing page',
            })}
          </Heading>
          <AppStoreContent>
            <BodyText variant="large" align="center">
              {intl.formatMessage({
                id: 'C7CFHt64GVQP-cWAf0OO',
                defaultMessage: 'Manage your existing orders with the easy to use app.',
                description: 'Body text for Afterpay app section on landing page',
              })}
            </BodyText>
            <ColumnContainer>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/1aab092" target="_blank" rel="noopener">
                <StoreImage id="qy1f0pkl6sbukmkbwayf" height="48" />
              </AppLinks>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/feb04c69" target="_blank" rel="noopener">
                <StoreImage id="p0qt65l8f6tzobhdkh" height="48" />
              </AppLinks>
            </ColumnContainer>
          </AppStoreContent>

        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <SubSection>
            <BodyText variant="large" align="center">
              {intl.formatMessage({
                id: '_BCPQoJExJZGVf4GqoJu',
                defaultMessage: 'Deals of the week',
                description: 'Section heading for deals on Afterpay landing page',
              })}
            </BodyText>
            <BuyNowContent>
              <OfferList maxOffers={4} filters={listFilters} />
            </BuyNowContent>
            <SectionCTA kind="primary" href="/">
              {intl.formatMessage({
                id: 'QoqqA3MJ3LlWTr_S7Y9I',
                defaultMessage: 'Discover all amazing deals',
                description: 'CTA button to view all deals on Afterpay landing page',
              })}
            </SectionCTA>
          </SubSection>
        </InnerSection>
      </Section>
    </div>
  )
}

const mapStateToProps = (state: App.State) => {
  const currency = state.geo.currentCurrency

  return {
    currency,
    isAfterPayBpEnabled: isAfterPayBpEnabled(currency),
  }
}

export default connect(mapStateToProps)(AfterPayLandingPage)
